import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, StatCard, Pill } from "@/components/croc/ui";
import { fmtUsd, fmtNum, pct, shortAddr } from "@/lib/format";
import api from "@/lib/api";
import { CONTRACTS, CHAIN } from "@/contracts/croc";
import { Slider } from "@/components/ui/slider";
import { ShieldCheck, KeyRound, Gauge, Coins, Trophy, Lock, ExternalLink, Cpu, Radio, Sparkles, BadgePercent, Network } from "lucide-react";

export default function Admin() {
  const { address, getBrowserProvider, isDemo, connect } = useWallet();
  const { data: cfg } = usePolling(() => api.adminConfig(), [], 300000);
  const { data: pubCfg } = usePolling(() => api.config(), [], 15000);
  const adminAddr = cfg?.admin_address;
  const isAdminWallet = address && adminAddr && address.toLowerCase() === adminAddr.toLowerCase();

  const [token, setToken] = useState(() => sessionStorage.getItem("croc_admin_token") || "");
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rateBps, setRateBps] = useState(32);
  const [topup, setTopup] = useState("25000");
  const [pool, setPool] = useState("king");
  const [winners, setWinners] = useState(["", "", ""]);
  const [testMins, setTestMins] = useState("120");
  const [chainStat, setChainStat] = useState(null);
  const [lastTx, setLastTx] = useState(null);
  const [mkt, setMkt] = useState(null);
  const [grant, setGrant] = useState({ address: "", points: "1000", pct: 0.12 });
  const [mktBudget, setMktBudget] = useState("5000");
  const [lvlPcts, setLvlPcts] = useState([]);

  const loadOverview = async (t) => {
    try {
      const o = await api.adminOverview(t || token);
      setOverview(o);
      setRateBps(Math.round((o.base_daily_rate || 0.0032) * 10000));
      api.adminChainStatus(t || token).then(setChainStat).catch(() => {});
      api.adminMktStatus(t || token).then(setMkt).catch(() => {});
      api.config().then((c) => setLvlPcts((c.levels || []).map((l) => String(+(l.pct * 100).toFixed(3))))).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 401) { setToken(""); sessionStorage.removeItem("croc_admin_token"); }
    }
  };
  useEffect(() => { if (token) loadOverview(); /* eslint-disable-next-line */ }, [token]);

  const authenticate = async () => {
    const provider = getBrowserProvider();
    if (!provider) return toast.error("Connect the admin wallet (MetaMask/WalletConnect) to sign in");
    try {
      setBusy(true);
      const { message } = await api.adminChallenge(address);
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);
      const res = await api.adminVerify({ address, signature, message });
      setToken(res.token);
      sessionStorage.setItem("croc_admin_token", res.token);
      toast.success("Admin authenticated");
      loadOverview(res.token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  const saveRate = async () => {
    try { setBusy(true); await api.adminSetRate(rateBps, token); toast.success(`Daily rate set to ${(rateBps / 100).toFixed(2)}%`); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doTopup = async () => {
    try { setBusy(true); await api.adminTopup(parseFloat(topup) || 0, token); toast.success(`Revenue pool topped up ${fmtUsd(parseFloat(topup))}`); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const toggleTestSpeed = async (enabled) => {
    try { setBusy(true); const r = await api.adminTestSpeed(enabled, parseInt(testMins) || 120, token);
      toast.success(enabled ? `Test speed ON — 1 day = 5 min (until ${new Date(r.expires_at).toLocaleTimeString()})` : "Test speed OFF — normal daily accrual");
      loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const saveWinners = async () => {
    try { setBusy(true); await api.adminWinners(pool, winners.filter(Boolean), token); toast.success(`${pool} pool winners set`); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doLevelConfig = async () => {
    const pcts = lvlPcts.map((v) => (parseFloat(v) || 0) / 100);
    if (pcts.length !== 21) return toast.error("Need all 21 level percentages");
    try { setBusy(true); const r = await api.adminLevelConfig({ pcts }, token); toast.success(`Override %s applied${r.onchain_tx ? " (mirrored on-chain)" : ""}`); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doGrant = async () => {
    try { setBusy(true); await api.adminMktGrant({ address: grant.address, points: parseFloat(grant.points) || 0, pct: grant.pct }, token); toast.success("Points granted"); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); } finally { setBusy(false); }
  };
  const doBudget = async () => {
    try { setBusy(true); const r = await api.adminMktBudget({ amount: parseFloat(mktBudget) || 0 }, token); toast.success(`Budget topped up to ${fmtUsd(r.budget_usd)}`); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doToggle = async () => {
    try { setBusy(true); const r = await api.adminMktToggle({ enabled: !mkt?.enabled }, token); toast.success(r.enabled ? "Marketing points enabled" : "Marketing points paused"); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!address) {
    return (
      <div>
        <SectionTitle eyebrow="Operator Access" title="Admin Console" testid="admin-title" />
        <Panel glow="gold" className="text-center py-16 max-w-lg mx-auto" data-testid="admin-connect">
          <div className="w-14 h-14 rounded-2xl bg-[#1CFD4C]/12 border border-[#1CFD4C]/30 grid place-items-center mx-auto mb-4">
            <Lock className="w-7 h-7 gold-text" />
          </div>
          <p className="text-white font-display font-bold text-lg">Connect the admin wallet</p>
          <p className="text-muted-foreground text-sm mt-1 mb-1">Only the authorized operator wallet can open this console.</p>
          <p className="text-[11px] text-muted-foreground font-num mb-5">Admin: {adminAddr ? shortAddr(adminAddr) : "…"}</p>
          <button data-testid="admin-connect-button" onClick={() => connect()} disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-6 py-3 font-bold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">
            <ShieldCheck className="w-4 h-4" /> Connect Wallet
          </button>
        </Panel>
      </div>
    );
  }

  if (!isAdminWallet) {
    return (
      <div>
        <SectionTitle eyebrow="Operator Access" title="Admin Console" testid="admin-title" />
        <Panel className="text-center py-16" data-testid="admin-denied">
          <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-white font-medium">Admin-only area</p>
          <p className="text-muted-foreground text-sm mt-1">
            Connect the authorized admin wallet to continue.
          </p>
          <p className="text-[11px] text-muted-foreground mt-3 font-num">Admin: {adminAddr ? shortAddr(adminAddr) : "…"}</p>
          {address && <p className="text-[11px] text-muted-foreground mt-1 font-num" data-testid="admin-connected-addr">Connected: {shortAddr(address)}</p>}
          {isDemo && <Pill tone="danger" className="mt-3">Demo wallet cannot sign — use a real admin wallet</Pill>}
        </Panel>
      </div>
    );
  }

  if (!token) {
    return (
      <div>
        <SectionTitle eyebrow="Operator Access" title="Admin Console" testid="admin-title" />
        <Panel glow="gold" className="text-center py-16 max-w-lg mx-auto" data-testid="admin-signin">
          <div className="w-14 h-14 rounded-2xl bg-[#1CFD4C]/12 border border-[#1CFD4C]/30 grid place-items-center mx-auto mb-4">
            <KeyRound className="w-7 h-7 gold-text" />
          </div>
          <p className="text-white font-display font-bold text-lg">Sign in with your wallet</p>
          <p className="text-muted-foreground text-sm mt-1 mb-5">Prove ownership of the admin address by signing a message. No gas, no password.</p>
          <button data-testid="admin-authenticate-button" onClick={authenticate} disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-6 py-3 font-bold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">
            <ShieldCheck className="w-4 h-4" /> {busy ? "Signing…" : "Authenticate"}
          </button>
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <SectionTitle
        eyebrow="Operator Access · Wallet-signature secured"
        title="Admin Console"
        right={<Pill tone="emerald"><ShieldCheck className="w-3 h-3" /> Authenticated</Pill>}
        testid="admin-title"
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Base Daily Rate" value={pct((overview?.base_daily_rate ?? 0) * 100)} icon={Gauge} accent="gold" testid="admin-stat-rate" />
        <StatCard label="Revenue Top-up" value={fmtUsd(overview?.revenue_topup)} icon={Coins} accent="emerald" testid="admin-stat-topup" />
        <StatCard label="Users" value={fmtNum(overview?.users, 0)} icon={ShieldCheck} accent="gold" testid="admin-stat-users" />
        <StatCard label="Turnover" value={fmtUsd(overview?.turnover)} icon={Trophy} accent="emerald" testid="admin-stat-turnover" />
        <StatCard label="Creator Fund" value={fmtUsd(overview?.creator_fund)} sub={`8% of every top-up`} icon={Coins} accent="gold" testid="admin-stat-creator" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel data-testid="admin-rate-panel">
          <div className="flex items-center gap-2 mb-4"><Gauge className="w-4 h-4 gold-text" /><span className="font-display font-bold text-white">Dynamic Daily Rate</span></div>
          <div className="flex items-baseline justify-between mb-3">
            <span className="font-num font-black text-3xl gold-text">{(rateBps / 100).toFixed(2)}%</span>
            <span className="text-xs text-muted-foreground">cap 0.50%</span>
          </div>
          <Slider data-testid="admin-rate-slider" value={[rateBps]} min={0} max={50} step={1} onValueChange={(v) => setRateBps(v[0])} />
          <button data-testid="admin-save-rate-button" onClick={saveRate} disabled={busy}
            className="mt-4 w-full rounded-xl bg-[#1CFD4C] py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">Update Daily Rate</button>
        </Panel>

        <Panel data-testid="admin-topup-panel">
          <div className="flex items-center gap-2 mb-4"><Coins className="w-4 h-4 emerald-text" /><span className="font-display font-bold text-white">Revenue Pool Top-up</span></div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-3 flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <input data-testid="admin-topup-input" type="number" value={topup} onChange={(e) => setTopup(e.target.value)} className="bg-transparent font-num font-bold text-white w-full outline-none" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Adds realized revenue to fund distributions and improve the coverage ratio.</p>
          <button data-testid="admin-topup-button" onClick={doTopup} disabled={busy}
            className="mt-4 w-full rounded-xl bg-[#10B981] py-2.5 font-semibold text-[#050806] hover:bg-[#34D399] transition-colors disabled:opacity-60">Top Up Revenue Pool</button>
        </Panel>

        <Panel data-testid="admin-winners-panel">
          <div className="flex items-center gap-2 mb-4"><Trophy className="w-4 h-4 gold-text" /><span className="font-display font-bold text-white">Croc Pool Winners</span></div>
          <div className="flex gap-2 mb-3">
            {[["king", "King Croc (#1)"], ["baby", "Baby Croc (#2)"]].map(([p, label]) => (
              <button key={p} data-testid={`admin-pool-${p}`} onClick={() => setPool(p)}
                className={`flex-1 rounded-lg py-2 text-xs border ${pool === p ? "border-[#1CFD4C]/50 bg-[#1CFD4C]/10 text-white" : "border-white/10 text-muted-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          <input data-testid="admin-winner-0" value={winners[0]}
            onChange={(e) => setWinners((w) => w.map((x, j) => (j === 0 ? e.target.value : x)))}
            placeholder={`${pool === "king" ? "King Croc" : "Baby Croc"} winner 0x… (blank = auto by rank)`}
            className="w-full mb-2 rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2 text-sm font-num text-white outline-none focus:border-[#1CFD4C]/50" />
          <p className="text-[11px] text-muted-foreground mb-2">Leave blank to auto-pick #1 (King, 20%) and #2 (Baby, 10%) from the leaderboard.</p>
          <button data-testid="admin-save-winners-button" onClick={saveWinners} disabled={busy}
            className="mt-1 w-full rounded-xl bg-[#1CFD4C] py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">Set {pool === "king" ? "King" : "Baby"} Croc Winner</button>
        </Panel>
      </div>

      <Panel className="mt-6" data-testid="admin-testspeed-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Gauge className="w-4 h-4 gold-text" /><span className="font-display font-bold text-white">Test Speed — Fast ROI &amp; Override</span></div>
          {pubCfg?.test_speed?.active ? (
            <Pill tone="gold" data-testid="admin-testspeed-status">● ON · 1 day = 5 min{pubCfg.test_speed.expires_at ? ` · until ${new Date(pubCfg.test_speed.expires_at).toLocaleTimeString()}` : ""}</Pill>
          ) : (
            <Pill tone="emerald" data-testid="admin-testspeed-status">Normal · 1 day = 24 h</Pill>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          For testing only: compresses time so 1 day of ROI and override accrual happens every 5 real minutes.
          Auto-reverts to normal after the window below — so it turns itself back on its own.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-xl border border-white/10 bg-[#090E0B] px-3 py-2 flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Auto-off after</span>
            <input data-testid="admin-testspeed-mins" type="number" value={testMins} onChange={(e) => setTestMins(e.target.value)}
              className="bg-transparent font-num font-bold text-white w-16 outline-none" />
            <span className="text-[11px] text-muted-foreground">min</span>
          </div>
          <button data-testid="admin-testspeed-on" onClick={() => toggleTestSpeed(true)} disabled={busy}
            className="rounded-xl bg-[#1CFD4C] px-5 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">Enable Test Speed</button>
          <button data-testid="admin-testspeed-off" onClick={() => toggleTestSpeed(false)} disabled={busy}
            className="rounded-xl border border-white/15 px-5 py-2.5 font-semibold text-white hover:bg-white/5 transition-colors disabled:opacity-60">Back to Normal</button>
        </div>
      </Panel>

      <Panel className="mt-6" data-testid="admin-chain-actions">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cpu className="w-4 h-4 emerald-text" />
            <span className="font-display font-bold text-white">On-chain Actions</span>
          </div>
          {chainStat?.enabled ? (
            <Pill tone="emerald" data-testid="admin-writer-status">
              <Radio className="w-3 h-3" /> Operator {shortAddr(chainStat.operator)} · {fmtNum(chainStat.balance_bnb, 4)} tBNB
            </Pill>
          ) : (
            <Pill tone="danger" data-testid="admin-writer-status">Operator key not configured</Pill>
          )}
        </div>

        <div data-testid="admin-auto-distribution" className="rounded-xl border border-[#1CFD4C]/25 bg-[#1CFD4C]/[0.05] p-4 flex items-start gap-3">
          <Trophy className="w-5 h-5 gold-text shrink-0 mt-0.5" />
          <div>
            <div className="text-white font-medium text-sm">All income is distributed automatically</div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
              ROI, override (level income), rank commissions and prediction bonuses are credited to members automatically every day at
              <span className="gold-text font-semibold"> 00:00 GST (UAE)</span>, and the reward pools are distributed
              on-chain automatically at <span className="gold-text font-semibold">01:30 GST (UAE)</span>. No manual action is required.
            </p>
            <div className="mt-2 text-[11px] font-num text-muted-foreground">
              Operator signing: {chainStat?.enabled ? <span className="emerald-text">enabled (auto on-chain mirrors)</span> : <span className="text-red-400">disabled — set OPERATOR_PRIVATE_KEY</span>}
            </div>
          </div>
        </div>

        {lastTx?.tx && (
          <a data-testid="admin-last-tx" href={`${CHAIN.explorer}/tx/${lastTx.tx}`} target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm emerald-text hover:underline font-num">
            <ExternalLink className="w-3.5 h-3.5" /> Last tx: {shortAddr(lastTx.tx)} {lastTx.status === 1 ? "· confirmed" : ""}
          </a>
        )}
      </Panel>

      <Panel className="mt-6" data-testid="admin-mkt">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 gold-text" />
            <span className="font-display font-bold text-white">Marketing Points · Off-chain Budget</span>
          </div>
          <div className="flex items-center gap-2">
            <Pill tone="gold" data-testid="admin-mkt-budget-pill">Budget {fmtUsd(mkt?.budget_usd)}</Pill>
            <button data-testid="admin-mkt-toggle" onClick={doToggle} disabled={busy}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${mkt?.enabled ? "bg-red-500/15 text-[#4DFF74] border border-red-500/40" : "bg-[#10B981]/15 emerald-text border border-[#10B981]/40"}`}>
              {mkt?.enabled ? "Stop Feature" : "Start Feature"}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="flex items-center gap-2 mb-3"><BadgePercent className="w-4 h-4 emerald-text" /><span className="text-white font-medium text-sm">Grant Points to a User</span></div>
            <input data-testid="admin-mkt-address" value={grant.address} onChange={(e) => setGrant({ ...grant, address: e.target.value })} placeholder="User address 0x…"
              className="w-full mb-2 rounded-lg border border-white/10 bg-[#0B120E] px-3 py-2 text-sm font-num text-white outline-none focus:border-[#1CFD4C]/50" />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input data-testid="admin-mkt-points" type="number" value={grant.points} onChange={(e) => setGrant({ ...grant, points: e.target.value })} placeholder="Points (1000/500…)"
                className="rounded-lg border border-white/10 bg-[#0B120E] px-3 py-2 text-sm font-num text-white outline-none focus:border-[#1CFD4C]/50" />
              <select data-testid="admin-mkt-pct" value={grant.pct} onChange={(e) => setGrant({ ...grant, pct: parseFloat(e.target.value) })}
                className="rounded-lg border border-white/10 bg-[#0B120E] px-3 py-2 text-sm font-num text-white outline-none focus:border-[#1CFD4C]/50">
                {[0.12, 0.10, 0.07, 0.05, 0.03].map((p) => <option key={p} value={p}>{Math.round(p * 100)}% redeem</option>)}
              </select>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">On downline business, the user redeems their % of their point balance into USDT. Referring a lower-% member leaves the upline only the differential.</p>
            <button data-testid="admin-mkt-grant-button" onClick={doGrant} disabled={busy || !grant.address}
              className="w-full rounded-xl bg-[#1CFD4C] py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">Grant Points</button>
          </div>

          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4">
            <div className="flex items-center gap-2 mb-3"><Coins className="w-4 h-4 gold-text" /><span className="text-white font-medium text-sm">Marketing Budget (USDT)</span></div>
            <div className="rounded-lg border border-white/10 bg-[#0B120E] px-3 py-2 flex items-center gap-2 mb-2">
              <span className="text-muted-foreground text-sm">$</span>
              <input data-testid="admin-mkt-budget-input" type="number" value={mktBudget} onChange={(e) => setMktBudget(e.target.value)}
                className="bg-transparent font-num font-bold text-white w-full outline-none" />
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">Tops up the separate budget that funds all point→USDT redemptions. When it runs out, redemptions stop automatically.</p>
            <button data-testid="admin-mkt-budget-button" onClick={doBudget} disabled={busy}
              className="w-full rounded-xl bg-[#10B981] py-2.5 font-semibold text-[#050806] hover:bg-[#34D399] transition-colors disabled:opacity-60">Add to Budget</button>
          </div>
        </div>

        {(mkt?.grants?.length ?? 0) > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Active Grants</div>
            <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
              {mkt.grants.map((g, i) => (
                <div key={g.address || i} className="grid grid-cols-4 items-center rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2" data-testid={`admin-mkt-grant-${i}`}>
                  <span className="font-num text-xs text-white">{shortAddr(g.address)}</span>
                  <span className="font-num text-xs text-muted-foreground">{Math.round((g.pct || 0) * 100)}%</span>
                  <span className="font-num text-xs gold-text">{fmtNum(g.points_balance, 0)} pts</span>
                  <span className="font-num text-xs emerald-text text-right">{fmtUsd(g.accrued_usdt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>

      <Panel className="mt-6" data-testid="admin-level-config">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 gold-text" />
            <span className="font-display font-bold text-white">Override Income % Editor</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-level-reset" onClick={() => setLvlPcts(["20","10","5","3","2.5","1","1","1","1","1","0.5","0.5","0.5","0.5","0.5","0.5","0.5","0.5","0.5","0.5","0.5"])}
              className="rounded-lg px-3 py-1.5 text-xs bg-[#0B120E] border border-white/10 text-muted-foreground hover:text-white transition-colors">Defaults</button>
            <button data-testid="admin-level-apply" onClick={doLevelConfig} disabled={busy}
              className="rounded-lg bg-[#1CFD4C] px-4 py-1.5 text-xs font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60">Apply to Chain</button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Set the override income % for each of the 21 levels. Applies to payouts immediately and mirrors on-chain via <span className="font-num">setLevelConfig</span>.</p>
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
          {lvlPcts.map((v, i) => (
            <div key={i} className="rounded-lg border border-white/10 bg-[#090E0B] px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">L{i + 1}</div>
              <input data-testid={`admin-level-input-${i + 1}`} type="number" step="0.1" value={v}
                onChange={(e) => setLvlPcts((p) => p.map((x, j) => (j === i ? e.target.value : x)))}
                className="w-full bg-transparent font-num font-bold text-white outline-none text-sm" />
            </div>
          ))}
        </div>
      </Panel>

      <Panel className="mt-6" data-testid="admin-contracts">
        <div className="font-display font-bold text-white mb-3">Deployed Contracts · {CHAIN.name}</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {Object.entries(CONTRACTS).map(([name, addr]) => (
            <a key={name} href={`${CHAIN.explorer}/address/${addr}`} target="_blank" rel="noreferrer"
              className="rounded-xl border border-white/10 bg-[#090E0B] p-3 hover:border-[#1CFD4C]/50 transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">{name}</span>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <div className="text-[11px] font-num text-muted-foreground mt-1">{shortAddr(addr)}</div>
            </a>
          ))}
        </div>
      </Panel>
    </div>
  );
}
